import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  SPECIALS_GRID_LIMIT,
  SpecialCard,
  SpecialsGrid,
  normalizeSpecialsDisplayName,
} from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Skeleton,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import { useMemo, useState } from 'react';

import { SpecialCard as OldSpecialCard } from '@jane/shared-ecomm/components';
import {
  SPECIALS_PAGE_LIMIT,
  useMenu,
  useSpecials,
} from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';

import styles from './MenuSpecialsGrid.module.css';

export const MenuSpecialsGrid = () => {
  const specialsRedesignMenu = useFlag(FLAGS.c3SpecialsRedesignMenu);
  const SPECIALS_LIMIT_TO_USE = specialsRedesignMenu ? SPECIALS_GRID_LIMIT : 6;

  const [showFullPage, setShowFullPage] = useState(false);
  const isMobile = useMobileMediaQuery({});
  const { store, paths } = useMenu();
  const {
    fetchNextSpecialsPage,
    hasNextSpecialsPage,
    loadingSpecialsCards,
    refetchSpecialsPage,
    specialsCards = [],
  } = useSpecials();

  const handleScroll = () => {
    const element = document.getElementById('specials-grid');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const onViewLess = () => {
    setShowFullPage(false);
    refetchSpecialsPage();
    handleScroll();
  };

  const onViewMore = () => {
    if (!showFullPage) {
      setShowFullPage(true);
    } else {
      fetchNextSpecialsPage();
    }
  };

  const rowName = normalizeSpecialsDisplayName(
    store.specials_row_configs?.custom_display_name
  );

  const nextBreadcrumbs = [
    {
      title: rowName,
      url: paths.menuCategory('specials'),
    },
  ];

  const filteredSpecials = useMemo(() => {
    return specialsCards.filter(
      (special) => special.hidden_from_page === false
    );
  }, [specialsCards]);

  const showViewMore =
    filteredSpecials &&
    ((filteredSpecials.length > SPECIALS_LIMIT_TO_USE && !showFullPage) ||
      hasNextSpecialsPage);

  const showViewLess =
    !showViewMore && filteredSpecials.length > SPECIALS_PAGE_LIMIT;

  const specialCardsToRender = showFullPage
    ? filteredSpecials
    : filteredSpecials.slice(0, SPECIALS_LIMIT_TO_USE);

  // TODO: Clean up when specialsRedesignMenu is fully rolled out
  const Grid = specialsRedesignMenu ? SpecialsGrid : Box;

  return (
    <Flex px={isMobile ? 24 : 64} flexDirection="column" alignItems="center">
      <Grid
        className={
          !specialsRedesignMenu ? styles['oldSpecialsGrid'] : undefined
        }
        {...(specialsRedesignMenu && { mobileColumns: 'auto' })}
        id="specials-grid"
      >
        {loadingSpecialsCards && !specialsCards?.length
          ? Array.from({ length: SPECIALS_LIMIT_TO_USE }, (_, i) => (
              <Skeleton.Bone height={240} width="100%" key={i} />
            ))
          : specialCardsToRender.map((special, index) =>
              specialsRedesignMenu ? (
                <SpecialCard
                  key={special.id}
                  breadcrumbs={nextBreadcrumbs}
                  customSpecialBadge={special?.custom_badge}
                  discountAmount={special?.discount_amount}
                  discountType={special?.discount_type}
                  imageSrc={special?.photo?.urls?.original}
                  reservationModeLabels={store?.reservation_mode_labels}
                  reservationModes={special?.reservation_modes}
                  specialType={special?.special_type}
                  storeComplianceLanguageSettings={
                    store?.store_compliance_language_settings
                  }
                  title={special.title}
                  to={paths.menuSpecial({
                    id: special.id,
                    title: special.title,
                  })}
                  onClick={() =>
                    track({
                      event: EventNames.StoreSpecialTap,
                      source: 'Specials Row',
                      specialRankOnList: index + 1,
                      storeName: store.name,
                      storeId: store.id,
                      specialId: special.id,
                      specialType: special.special_type,
                    })
                  }
                />
              ) : (
                <OldSpecialCard
                  key={special.id}
                  breadcrumbs={nextBreadcrumbs}
                  special={special}
                  store={store}
                  to={paths.menuSpecial({
                    id: special.id,
                    title: special.title,
                  })}
                  onClick={() =>
                    track({
                      event: EventNames.StoreSpecialTap,
                      source: 'Specials Row',
                      specialRankOnList: index + 1,
                      storeName: store.name,
                      storeId: store.id,
                      specialId: special.id,
                      specialType: special.special_type,
                    })
                  }
                />
              )
            )}
      </Grid>
      {!loadingSpecialsCards && (showViewLess || showViewMore) && (
        <Button
          aria-label={
            showViewMore ? 'View more specials' : 'View less specials'
          }
          label={showViewMore ? 'View more' : 'View less'}
          onClick={showViewMore ? onViewMore : onViewLess}
          full={isMobile}
          my={isMobile ? 24 : 48}
        />
      )}
    </Flex>
  );
};
